import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ContractType = "company" | "asset";
export type ContractStatus = "active" | "expiring_soon" | "expired" | "draft";
export type SLAAppliesTo = "work_orders" | "tickets" | "both";
type TimeUnit = "hours" | "days";
type CoverageType = "business_hours" | "extended_business_hours" | "around_the_clock";

export interface PanelSLALevel {
  id: string;
  priority: string;
  responseTime: number;
  responseUnit: string;
  resolutionTime: number;
  resolutionUnit: string;
}

export interface PanelContract {
  id: string;
  contractNumber: string;
  type: ContractType;
  status: ContractStatus;
  companyName?: string;
  assetName?: string;
  assetId?: string;
  effectiveDate: string;
  expiryDate: string;
  paymentTerms: string;
  currency?: string;
  exchangeRateType?: "live" | "fixed";
  exchangeRate?: number;
  laborRate: number;
  laborDiscount?: number;
  laborMarginCap?: number;
  partsDiscountRate: number;
  partsMarginCap?: number;
  serviceDiscount?: number;
  serviceMarginCap?: number;
  travelRate: number;
  travelDiscount?: number;
  travelMarginCap?: number;
  slaLevels: PanelSLALevel[];
}

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ContractStatus, { bg: string; color: string; label: string }> = {
  active:        { bg: "#ccefeb", color: "#00b09b", label: "ACTIVE" },
  expiring_soon: { bg: "#fff0e6", color: "#FF7A00", label: "EXPIRING SOON" },
  expired:       { bg: "#fde5f0", color: "#ee0064", label: "EXPIRED" },
  draft:         { bg: "#f3f4f6", color: "#748094", label: "DRAFT" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
    <circle cx="7" cy="7" r="5.5" />
    <path d="M7 6.5v4" />
    <circle cx="7" cy="4.5" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

const ContractDocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="1" width="10" height="12" rx="1.5" />
    <path d="M4 5h6M4 7.5h4M4 10h2.5" />
  </svg>
);

// ─── Badges ───────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ContractStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
      background: cfg.bg, color: cfg.color,
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

function TypeBadge({ type }: { type: ContractType }) {
  const isAsset = type === "asset";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
      background: isAsset ? "#e6e8fe" : "#f3f4f6",
      color: isAsset ? "#0517f8" : "#748094",
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {isAsset ? "Asset" : "Company"}
    </span>
  );
}

// ─── Panel sub-components ─────────────────────────────────────────────────────

function PanelSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{
        margin: "0 0 8px", fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
        color: "#748094", fontFamily: "var(--font-family-base)",
      }}>
        {title}
      </p>
      <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

function PanelRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "9px 12px", borderBottom: "1px solid var(--color-border-primary)",
    }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
        {label}
      </span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
        {value}
      </span>
    </div>
  );
}

// ─── SLA Toggle ───────────────────────────────────────────────────────────────

export function SLAToggle({ active, onChange }: { active: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!active)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "2px 8px 2px 4px", border: "none", cursor: "pointer",
        borderRadius: 20, background: active ? "#e6f9f1" : "#f1f3f5",
        transition: "background 150ms ease",
      }}
    >
      <span style={{
        width: 28, height: 16, borderRadius: 8, position: "relative",
        background: active ? "#10b981" : "#cbd5e0",
        transition: "background 150ms ease", flexShrink: 0,
      }}>
        <span style={{
          position: "absolute", top: 2, left: active ? 14 : 2,
          width: 12, height: 12, borderRadius: "50%", background: "#fff",
          transition: "left 150ms ease",
        }} />
      </span>
      <span style={{
        fontSize: 11, fontWeight: 700, fontFamily: "var(--font-family-base)",
        letterSpacing: "-0.2px", color: active ? "#065f46" : "#748094",
      }}>
        {active ? "Active" : "Inactive"}
      </span>
    </button>
  );
}

// ─── Contract SLA Accordion ───────────────────────────────────────────────────

type BusinessHoursType = "business-hours" | "extended-business-hours" | "around-the-clock";

interface ContractSLAItem {
  id: string;
  name: string;
  description: string;
  targetValue: number;
  targetUnit: string;
  businessHours: BusinessHoursType;
  features: string[];
  active: boolean;
}

const FEATURE_LABEL: Record<string, string> = {
  "work-orders": "Work Orders",
  "tickets": "Tickets",
};

const DEFAULT_CONTRACT_SLA_ITEMS: ContractSLAItem[] = [
  { id: "sla-onsite",     name: "Time to Onsite",         description: "Time from work order creation until technician arrives on site", targetValue: 2,  targetUnit: "hours", businessHours: "business-hours", features: ["work-orders"],             active: true  },
  { id: "sla-response",   name: "Time to First Response", description: "Time from ticket creation until the first agent reply is sent",  targetValue: 4,  targetUnit: "hours", businessHours: "business-hours", features: ["tickets"],                 active: true  },
  { id: "sla-resolution", name: "Time to Resolution",     description: "Time from creation until the issue is fully resolved",           targetValue: 24, targetUnit: "hours", businessHours: "business-hours", features: ["work-orders", "tickets"],  active: true  },
];

export function ContractSLAAccordion() {
  const [items, setItems] = React.useState<ContractSLAItem[]>(DEFAULT_CONTRACT_SLA_ITEMS);
  const [openId, setOpenId] = React.useState<string | null>(null);

  function toggleActive(id: string) {
    setItems(prev => prev.map(i => i.id === id ? { ...i, active: !i.active } : i));
  }

  function updateTarget(id: string, field: "targetValue" | "targetUnit" | "businessHours", value: number | string) {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map(item => {
        const isOpen = openId === item.id;
        return (
          <div
            key={item.id}
            style={{ border: `1.5px solid ${isOpen ? "var(--color-content-brand)" : "var(--color-border-primary)"}`, borderRadius: 10, overflow: "hidden", transition: "border-color 120ms ease", opacity: item.active ? 1 : 0.6 }}
          >
            {/* Accordion header */}
            <div style={{ display: "flex", alignItems: "center", background: isOpen ? "color-mix(in srgb, var(--color-content-brand) 4%, var(--color-bg-primary))" : "var(--color-bg-primary)", transition: "background 120ms ease" }}>
              {/* Expand button — takes up most of the row */}
              <button
                onClick={() => setOpenId(isOpen ? null : item.id)}
                style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left", minWidth: 0 }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: isOpen ? "color-mix(in srgb, var(--color-content-brand) 10%, var(--color-bg-primary))" : "var(--color-bg-accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: isOpen ? "var(--color-content-brand)" : "var(--color-content-secondary)", transition: "all 120ms ease" }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="8" r="6" />
                    <path d="M8 5v3l2 1.5" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.14px" }}>
                      {item.name}
                    </span>
                    {item.features.map(f => (
                      <span key={f} style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 5px", background: "#EFF6FF", color: "#1D4ED8", fontFamily: "var(--font-family-base)", flexShrink: 0 }}>
                        {FEATURE_LABEL[f] ?? f}
                      </span>
                    ))}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                    {item.description}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", background: "var(--color-bg-accent)", border: "1px solid var(--color-border-primary)", borderRadius: 5, padding: "2px 8px", whiteSpace: "nowrap" }}>
                    {item.targetValue} {item.targetUnit}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms ease", flexShrink: 0 }}>
                    <path d="M3 5l4 4 4-4" />
                  </svg>
                </div>
              </button>
              {/* Toggle — sits outside the expand button */}
              <div style={{ padding: "0 14px 0 0", flexShrink: 0 }}>
                <SLAToggle active={item.active} onChange={() => toggleActive(item.id)} />
              </div>
            </div>

            {/* Expanded body */}
            {isOpen && (
              <div style={{ borderTop: "1px solid var(--color-border-primary)", padding: "14px 14px 16px", display: "flex", flexDirection: "column", gap: 14, background: "var(--color-bg-accent)" }}>
                {/* Time Target */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Time Target</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="number"
                      min={1}
                      value={item.targetValue}
                      onChange={e => updateTarget(item.id, "targetValue", Math.max(1, Number(e.target.value)))}
                      onClick={e => e.stopPropagation()}
                      style={{ width: 72, height: 34, borderRadius: 6, border: "1px solid var(--color-border-primary)", padding: "0 10px", fontSize: 13, fontWeight: 600, fontFamily: "var(--font-family-base)", color: "var(--color-content-primary)", background: "var(--color-bg-primary)", outline: "none", boxSizing: "border-box" }}
                    />
                    <select
                      value={item.targetUnit}
                      onChange={e => updateTarget(item.id, "targetUnit", e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ height: 34, borderRadius: 6, border: "1px solid var(--color-border-primary)", padding: "0 8px", fontSize: 13, fontWeight: 500, fontFamily: "var(--font-family-base)", color: "var(--color-content-primary)", background: "var(--color-bg-primary)", outline: "none", cursor: "pointer" }}
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                    <select
                      value={item.businessHours}
                      onChange={e => updateTarget(item.id, "businessHours", e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ height: 34, borderRadius: 6, border: "1px solid var(--color-border-primary)", padding: "0 8px", fontSize: 13, fontWeight: 500, fontFamily: "var(--font-family-base)", color: "var(--color-content-primary)", background: "var(--color-bg-primary)", outline: "none", cursor: "pointer" }}
                    >
                      <option value="business-hours">Business Hours</option>
                      <option value="extended-business-hours">Extended Business Hours</option>
                      <option value="around-the-clock">Around the Clock</option>
                    </select>
                  </div>
                </div>
                {/* Applies to */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Applies To</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {item.features.map(f => (
                      <span key={f} style={{ fontSize: 12, fontWeight: 700, borderRadius: 6, padding: "3px 10px", background: "#EFF6FF", color: "#1D4ED8", fontFamily: "var(--font-family-base)", border: "1.5px solid #BFDBFE" }}>
                        {FEATURE_LABEL[f] ?? f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Applies To Picker (kept for backward compat) ─────────────────────────────

const APPLIES_TO_OPTIONS: { value: SLAAppliesTo; label: string }[] = [
  { value: "work_orders", label: "Work Orders" },
  { value: "tickets",     label: "Tickets" },
  { value: "both",        label: "Both" },
];

export function AppliesToPicker({ value, onChange }: { value: SLAAppliesTo; onChange: (v: SLAAppliesTo) => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#748094", fontFamily: "var(--font-family-base)" }}>
        Applies to:
      </span>
      <div style={{ display: "inline-flex", border: "1px solid var(--color-border-primary)", borderRadius: 6, overflow: "hidden" }}>
        {APPLIES_TO_OPTIONS.map(({ value: v, label }, i) => {
          const active = value === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              style={{
                padding: "3px 9px",
                border: "none",
                borderRight: i < APPLIES_TO_OPTIONS.length - 1 ? "1px solid var(--color-border-primary)" : "none",
                cursor: "pointer",
                fontSize: 11, fontWeight: 700,
                fontFamily: "var(--font-family-base)",
                background: active ? "var(--color-bg-brand-dark)" : "transparent",
                color: active ? "#fff" : "#748094",
                transition: "all 100ms",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Contract Card ────────────────────────────────────────────────────────────

export function ContractCard({ contract, companyName, onClick }: { contract: PanelContract; companyName?: string; onClick: () => void }) {
  const [hovered, setHovered] = React.useState(false);
  const isAsset = contract.type === "asset";
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: "1px solid #E8EAED", borderRadius: 8,
        padding: "10px 12px", display: "flex", alignItems: "center", gap: 10,
        cursor: "pointer",
        background: hovered ? "#F9FAFB" : "#fff",
        transition: "background 80ms",
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: isAsset ? "#EFF6FF" : "#F0FDF4",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        color: isAsset ? "#0517F8" : "#16A34A",
      }}>
        <ContractDocIcon />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          fontSize: 13, fontWeight: 600, color: "#172B4D",
          fontFamily: "var(--font-family-base)", lineHeight: "18px",
          display: "block", marginBottom: 5,
        }}>
          {contract.contractNumber}
        </span>
        {isAsset && contract.assetName ? (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            padding: "2px 6px", borderRadius: 4,
            fontSize: 11, fontWeight: 500, color: "#0517f8",
            background: "#e6e8fe", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
          }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="1" width="8" height="8" rx="1.5" />
              <path d="M3.5 5h3M5 3.5v3" />
            </svg>
            {contract.assetName}
          </span>
        ) : (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            padding: "2px 6px", borderRadius: 4,
            fontSize: 11, fontWeight: 500, color: "#748094",
            background: "#f3f4f6", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
          }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="8" height="6.5" rx="1" />
              <path d="M3 3V2a2 2 0 014 0v1" />
            </svg>
            {companyName ?? contract.companyName ?? "Company"}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Contract Detail Panel ────────────────────────────────────────────────────

export function ContractDetailPanel({ contract, onClose }: { contract: PanelContract; onClose: () => void }) {

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 200 }}
      />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 520,
        background: "var(--color-bg-primary)", zIndex: 201,
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--color-border-primary)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <span style={{
            fontSize: 16, fontWeight: 700, letterSpacing: "-0.32px",
            color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
          }}>
            {contract.contractNumber}
          </span>
          <button
            onClick={onClose}
            style={{
              width: 28, height: 28, border: "none", background: "transparent",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: 6, color: "var(--color-content-secondary)",
            }}
          >
            <XIcon />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

          {/* Badges */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <TypeBadge type={contract.type} />
            <StatusBadge status={contract.status} />
          </div>

          {/* Asset override callout */}
          {contract.type === "asset" && (
            <div style={{
              padding: "10px 12px", borderRadius: 8, marginBottom: 20,
              background: "#e6f9f1", border: "1px solid #a7f3d0",
              fontSize: 12, fontWeight: 500, color: "#065f46",
              fontFamily: "var(--font-family-base)", display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <InfoIcon />
              <span>
                Overrides the company contract
                {contract.companyName ? <> for <strong>{contract.companyName}</strong></> : null}
                {" "}on quotes, invoices &amp; work orders
                {contract.assetName ? <> for <strong>{contract.assetName}</strong></> : null}.
              </span>
            </div>
          )}

          {/* Linked To */}
          {(contract.companyName || contract.assetName) && (
            <PanelSection title="Linked To">
              {contract.companyName && <PanelRow label="Company" value={contract.companyName} />}
              {contract.assetName   && <PanelRow label="Asset"   value={`${contract.assetName} · ${contract.assetId ?? ""}`} />}
            </PanelSection>
          )}

          {/* Period */}
          <PanelSection title="Period">
            <PanelRow label="Effective Date" value={formatDate(contract.effectiveDate)} />
            <PanelRow label="Expiry Date"    value={formatDate(contract.expiryDate)} />
            <PanelRow label="Payment Terms"  value={contract.paymentTerms} />
            {contract.currency && (
              <PanelRow label="Currency" value={contract.currency} />
            )}
            {contract.currency && contract.currency !== "EUR" && (
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "9px 12px", borderBottom: "1px solid var(--color-border-primary)",
              }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
                  Exchange Rate
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                    background: contract.exchangeRateType === "live" ? "#e6f9f1" : "#f3f4f6",
                    color: contract.exchangeRateType === "live" ? "#065f46" : "#748094",
                    fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.04em",
                  }}>
                    {contract.exchangeRateType === "live" ? "Live" : "Fixed"}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                    1 EUR = {contract.exchangeRate?.toFixed(4)} {contract.currency}
                  </span>
                </div>
              </div>
            )}
          </PanelSection>

          {/* Rates & Discounts */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px", color: "#748094", fontFamily: "var(--font-family-base)" }}>
              Rates &amp; Discounts
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

              {/* Labor */}
              <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "7px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Labor</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
                  <div style={{ padding: "9px 12px", borderRight: "1px solid var(--color-border-primary)" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Rate</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>€{contract.laborRate}/hr</div>
                  </div>
                  <div style={{ padding: "9px 12px", borderRight: "1px solid var(--color-border-primary)" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Discount</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{contract.laborDiscount ?? 0}%</div>
                  </div>
                  <div style={{ padding: "9px 12px" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Margin Cap</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{contract.laborMarginCap ?? 0}%</div>
                  </div>
                </div>
              </div>

              {/* Parts */}
              <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "7px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Parts</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                  <div style={{ padding: "9px 12px", borderRight: "1px solid var(--color-border-primary)" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Discount</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{contract.partsDiscountRate}%</div>
                  </div>
                  <div style={{ padding: "9px 12px" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Margin Cap</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{contract.partsMarginCap ?? 0}%</div>
                  </div>
                </div>
              </div>

              {/* Service */}
              <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "7px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Service</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                  <div style={{ padding: "9px 12px", borderRight: "1px solid var(--color-border-primary)" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Discount</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{contract.serviceDiscount ?? 0}%</div>
                  </div>
                  <div style={{ padding: "9px 12px" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Margin Cap</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{contract.serviceMarginCap ?? 0}%</div>
                  </div>
                </div>
              </div>

              {/* Travel */}
              <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "7px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Travel</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
                  <div style={{ padding: "9px 12px", borderRight: "1px solid var(--color-border-primary)" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Rate</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>€{contract.travelRate}/km</div>
                  </div>
                  <div style={{ padding: "9px 12px", borderRight: "1px solid var(--color-border-primary)" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Discount</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{contract.travelDiscount ?? 0}%</div>
                  </div>
                  <div style={{ padding: "9px 12px" }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>Margin Cap</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{contract.travelMarginCap ?? 0}%</div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* SLA Configuration */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px", color: "#748094", fontFamily: "var(--font-family-base)" }}>
              SLA Configuration
            </p>
            <ContractSLAAccordion />
          </div>

        </div>
      </div>
    </>
  );
}
